/* eslint-disable no-extra-boolean-cast */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { v4 as uuidv4 } from "uuid";

import {
  BooleanFilterBanner,
  Details,
  Error as ErrorComponent,
  ErrorTypes,
  Filter,
  FinderSearch,
  HeaderBar,
  List,
  ListItemHoverEvent,
  ListItemHoverHandler,
  Map,
  MapController,
  Message,
  OnSearchCompleteProps,
  PopupUpdateEvent,
  StyledDetailsWrapper,
  Utils,
} from "nyc-component-lib";
import { useAppContext } from "../../contexts/AppContext";
import {
  StyledFabContainer,
  StyledLocationsView,
  StyledListH2,
  StyledFilterH2,
} from "./Locations-styled";
import { LocationPageTypes } from "./Locations.types";

import { CalciteFab } from "@esri/calcite-components-react";
import "@esri/calcite-components/dist/components/calcite-fab";

// InertPolyfill
import "wicg-inert";

import Extent from "@arcgis/core/geometry/Extent";
import Point from "@arcgis/core/geometry/Point";
import Graphic from "@arcgis/core/Graphic";
import { getCookie } from "../../utils/readCookie";

const CSAT_TRIGGER_DELAY_MS = 2000;

const LocationsPage = ({
  sourceInfos,
  appTitle,
  eventState,
  evacZones,
  alternateAppTitle,
  alternateBannerText,
  fieldLabels,
  emergency,
  currEvacZone,
}: LocationPageTypes) => {
  const {
    updateMapView,
    mapView,
    searchTerm,
    updateSearchTerm,
    focalPoint,
    updateFocalPoint,
    filterWhere,
    updateFilterWhere,
    locationsFeatureLayer,
    updateMapHighlight,
    appConfig,
    isEmergency,
    layoutDir,
    updateLayoutDir,
  } = useAppContext();
  const { locationId } = useParams();
  const [bannerText, setBannerText] = useState<string>("");

  const navigate = useNavigate();
  const { t } = useTranslation();

  // Set true by onCloseDetails; consumed once locationId clears to start the debounce timer.
  const showCsatAfterDetailsClose = useRef(false);
  const csatTriggerTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Current open/visible state of each surface, checked before queuing so a close on one
  // side doesn't fire CSAT while the other is still open.
  const isDetailsModalOpenRef = useRef(false);
  const isMapPopupVisibleRef = useRef(false);

  const locationLayerInfo = useMemo(() => {
    return {
      titleFieldName: appConfig?.fields.locTitleFieldname || "",
      subtitleFieldName: appConfig?.fields.locSubtitleFieldname || "",
      objectIdFieldName: appConfig?.fields.locObjectIDFieldname || "FID",
      addressFieldName: appConfig?.fields.locAddressFieldname,
      address2FieldName: appConfig?.fields.locAddress2Fieldname,
      phoneFieldName: appConfig?.fields.locPhoneFieldname,
      websiteFieldName: appConfig?.fields.locWebsiteFieldname,
      domainFieldNames: appConfig?.fields.locLayerDomainFieldnames,
      serviceTypeFieldNames: appConfig?.fields.locServiceTypeFieldnames || [],
      locMessageFieldNames: appConfig?.fields.locMessageFieldnames,
      openHoursServiceTypes: appConfig?.fields.locOpenHoursServiceTypes || [],
      openHoursTimeWindows: appConfig?.fields.locOpenHoursTimeWindows || [],
      serviceTypeNotes: appConfig?.fields.locServiceTypeNotes || [],
      locTypeFieldName: appConfig?.fields.locTypeFieldname,
      servicesFormat: appConfig?.fields.servicesFormat || ("byvalue" as "byvalue" | "notnull"),
      accessibleFieldName: appConfig?.fields.locAccessibleFieldname || "",
      accessibleFieldValue: appConfig?.fields.locAccessibleFieldValue || "",
      petFriendlyFieldName: appConfig?.fields.locPetFriendlyFieldname || "",
      petFriendlyFieldValue: appConfig?.fields.locPetFriendlyFieldValue || "",
      allAgesFieldName: appConfig?.fields.locAllAgesFieldname || "",
      allAgesFieldValue: appConfig?.fields.locAllAgesFieldValue || "",
      entranceInformation: appConfig?.fields.locEntranceInformationFieldname || "",
      acceptsVouchersFieldName: appConfig?.fields.locAcceptsVouchersFieldname || "",
      acceptsVouchersFieldValue: appConfig?.fields.locAcceptsVouchersFieldValue || "",
    };
  }, [appConfig?.fields]);

  // const zoneFL = useMemo(() => {
  //   const returnFL = window.activeMapConfig?.zone_layer_url
  //     ? new FeatureLayer({
  //         url: window.activeMapConfig?.zone_layer_url,
  //       })
  //     : null;

  //   returnFL
  //     ?.queryFeatureCount()
  //     .then((res) => {
  //       console.log(res);
  //     })
  //     .catch((error) => {
  //       setError(new Error(t("locations.unknownError")));
  //       setErrorMessage(ErrorTypes.ZONES);
  //     });
  //   return returnFL;
  // }, [t]);

  // useTranslate issue with key `app.filter.categoryFilters.appId.arrayIndex.key` getting object array
  const categoryFiltersStringsArray = t(
    `app.filter.categoryFilters.${appConfig?.appId || "childcarenyc"}`,
    { returnObjects: true }
  );
  const distanceFilterItem = useMemo(() => {
    if (!appConfig?.filter.distanceFilter) {
      return undefined;
    }

    return {
      ...appConfig?.filter.distanceFilter,
      filter_label: t(`app.filter.distanceFilter.filter_label`),
      filter_any_label: t(`app.filter.distanceFilter.filter_any_label`),
    };
  }, [t, appConfig?.filter.distanceFilter]);

  const filterFields =
    appConfig?.filter.categoryFilters?.map((item, idx) => {
      return {
        ...item,
        ...categoryFiltersStringsArray[idx],
      };
    }) || [];

  const booleanFilters = useMemo(() => {
    const booleanFiltersStringsArray = t(
      `app.filter.booleanFilters.${appConfig?.appId || "childcarenyc"}`,
      { returnObjects: true }
    );

    return (
      appConfig?.filter?.booleanFilters
        ?.filter((item) => {
          return !item.where_clause_or;
        })
        .map((item, idx) => {
          return {
            ...item,
            ...booleanFiltersStringsArray[idx],
          };
        }) || []
    );
  }, [appConfig?.filter?.booleanFilters, appConfig?.appId, t]);

  const booleanOrFilters = useMemo(() => {
    const booleanFiltersStringsArray = t(
      `app.filter.booleanOrFilters.${appConfig?.appId || "childcarenyc"}`,
      { returnObjects: true }
    );

    return (
      appConfig?.filter?.booleanFilters
        ?.filter((item) => {
          return item.where_clause_or;
        })
        .map((item, idx) => {
          return {
            ...item,
            ...booleanFiltersStringsArray[idx],
          };
        }) || []
    );
  }, [appConfig?.filter?.booleanFilters, appConfig?.appId, t]);

  const initialSearchBufferDistance = window.activeMapConfig?.search.searchInitialBufferDistance;

  const initialMapScale = 36111.909643; // LOD 14: https://developers.arcgis.com/documentation/mapping-apis-and-services/reference/zoom-levels-and-scale/

  const [initialCenter, setInitialCenter] = useState<Point>();
  const [initialExtent, setInitialExtent] = useState<Extent>();
  const [initialScale, setInitialScale] = useState<number>();
  const [mapReady, setIsMapReady] = useState<boolean>(false);
  const [currZone, setCurrZone] = useState<number | undefined>(currEvacZone || undefined);

  const searchString = useLocation()?.search;
  const mView = new URLSearchParams(searchString).get("mView");

  const [errorMessage, setErrorMessage] = useState<ErrorTypes | undefined>();
  const [error, setError] = useState<unknown | undefined>();

  const [popupHeight, setPopupHeight] = useState<number>(0);

  const onError = (message?: ErrorTypes, error?: unknown) => {
    if (error) console.error(t("locations.error", { error: error }));
    setError(error || new Error(t("locations.unknownError")));
    setErrorMessage(message || ErrorTypes.UNKNOWN);
  };

  useEffect(() => {
    setCurrZone(currEvacZone);
  }, [currEvacZone]);

  // trigger google translate re-translation if language is not set to English
  // called by this component on first render, and other components when they get new data that must be translated
  const updateGoogleTranslate = useCallback(() => {
    if (window.appConfig.useGoogleTranslate) {
      const googtrans = getCookie("googtrans");

      let toLang = null;
      if (googtrans) {
        const langs = googtrans.split("/");
        toLang = langs[2]; // e.g., "es"
      }

      const lang = toLang;
      updateLayoutDir(lang || "en");
      if (lang && lang !== "en") {
        const select = document.querySelector(".goog-te-combo") as HTMLSelectElement;
        if (select) {
          select.dispatchEvent(new Event("change"));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearchClear = () => {
    updateSearchTerm("");
    updateFocalPoint({});
    updateMapHighlight();
  };

  const onSearchComplete = ({ searchTerm, graphic, position }: OnSearchCompleteProps) => {
    updateSearchTerm(searchTerm);
    if (graphic?.attributes?.address_display === "No") {
      updateFocalPoint({
        graphic: undefined,
        position,
        isHidden: false,
      });
      // if graphic is undefined, route to /locations/x
      navigate({
        pathname: `/locations/${graphic?.attributes?.objectid}`,
        search: window.location.search,
      });
    } else {
      updateFocalPoint({
        graphic,
        position,
        isHidden: false,
      });
      navigate({
        pathname: "/locations",
        search: window.location.search,
      });
    }
  };

  const onSearchBlocked = () => {
    onError(ErrorTypes.SEARCH_BLOCKED);
  };

  const onSearchError = (error: unknown) => {
    onError(ErrorTypes.SEARCH, error);
  };

  const onMapError = (error: unknown) => {
    onError(ErrorTypes.MAP, error);
  };

  const onListError = useCallback((error: unknown) => {
    onError(ErrorTypes.LIST, error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchView = () => {
    const urlParams = new URLSearchParams(window.location.search);
    // Check if a parameter exists
    const hasParam = urlParams.has("i18n");
    // Get the value of a parameter
    let paramValue = null;
    if (hasParam) paramValue = urlParams.get("i18n");

    if (mView === "map") {
      // currently map, go to list
      navigate({ search: paramValue ? "?i18n=" + paramValue : "" });
    } else {
      // currently list, go to map
      navigate({ search: "?mView=map" + (paramValue ? "&i18n=" + paramValue : "") });
    }
  };

  // Pushes the event GTM listens for to decide when to show the CSAT survey.
  const pushShowCsatEvent = useCallback(() => {
    if (appConfig?.appId !== "childcarenyc") {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "show_csat_esri" });
  }, [appConfig?.appId]);

  const cancelCsatTrigger = useCallback(() => {
    if (csatTriggerTimeout.current) {
      clearTimeout(csatTriggerTimeout.current);
      csatTriggerTimeout.current = null;
    }
  }, []);

  const queueCsatTrigger = useCallback(() => {
    // Skip if the details modal or ESRI popup is still open on the other side.
    if (isDetailsModalOpenRef.current || isMapPopupVisibleRef.current) {
      return;
    }

    cancelCsatTrigger();
    csatTriggerTimeout.current = setTimeout(() => {
      csatTriggerTimeout.current = null;
      pushShowCsatEvent();
    }, CSAT_TRIGGER_DELAY_MS);
  }, [cancelCsatTrigger, pushShowCsatEvent]);

  // Queues on close (locationId clears); cancels if a provider opens before the delay elapses.
  useEffect(() => {
    isDetailsModalOpenRef.current = !!locationId;

    if (locationId) {
      cancelCsatTrigger();
      showCsatAfterDetailsClose.current = false;
    } else if (showCsatAfterDetailsClose.current) {
      showCsatAfterDetailsClose.current = false;
      queueCsatTrigger();
    }
  }, [locationId, queueCsatTrigger, cancelCsatTrigger]);

  // Mirrors the Details modal above; Map only reports true open/close transitions, so
  // switching directly between providers on the map does not count as a close.
  const onMapPopupVisibilityChange = useCallback(
    (visible: boolean) => {
      isMapPopupVisibleRef.current = visible;

      if (visible) {
        cancelCsatTrigger();
      } else {
        queueCsatTrigger();
      }
    },
    [cancelCsatTrigger, queueCsatTrigger]
  );

  // Clears any pending trigger on unmount.
  useEffect(() => {
    return () => {
      cancelCsatTrigger();
    };
  }, [cancelCsatTrigger]);

  const onCloseDetails = () => {
    // Modal is still mounted here; the effect above starts the debounce once locationId clears.
    showCsatAfterDetailsClose.current = true;

    navigate({
      pathname: "/locations",
      search: window.location.search,
    });

    updateGoogleTranslate();
  };

  const goToMap = (loc: Graphic) => {
    const go = async () => {
      if (mapView) {
        navigate({
          pathname: "/locations",
          search: "?mView=map",
        });

        if (mapView.popup && typeof mapView.popup.close === "function") {
          mapView.popup.close();
        }

        await MapController.goToLocations({
          mapView,
          layerUrl: `${window.activeMapConfig.loc_layer_url}`,
          features: [loc],
          updateMapHighlight,
          objectIdFieldname: appConfig?.fields.locObjectIDFieldname || "FID",
          showPopup: true,
        });
      }
    };
    go();
  };

  const onClickCardLink = (objId: string) => {
    navigate({
      pathname: `/locations/${objId}`,
      search: window.location.search,
    });
  };

  const onPopupShownHidden = (event: PopupUpdateEvent) => {
    setPopupHeight(event.newHeight);
  };

  const onListItemHover: ListItemHoverHandler = (hoverEvent: ListItemHoverEvent) => {
    updateMapHighlight();

    if (hoverEvent.isHovering) {
      if (mapView && hoverEvent.location) {
        MapController.showHighlight({
          mapView,
          layerUrl: `${window.activeMapConfig?.loc_layer_url}`,
          features: [hoverEvent.location],
          pan: false,
          updateMapHighlight: updateMapHighlight,
        });
      }
    }
  };

  useEffect(() => {
    const zoomToExtent = async () => {
      const whereClause = appConfig?.locations ? appConfig?.locations.defaultExtentWhere : "1=1";
      const extent = await MapController.queryLayerExtent({
        featureLayer: locationsFeatureLayer,
        where: whereClause,
      });
      setInitialExtent(extent);
    };

    if (!mapReady && focalPoint === undefined) {
      const extentZoom = setTimeout(() => {
        zoomToExtent();
      }, 1500);

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (event) => {
            clearTimeout(extentZoom);
            setInitialCenter(Utils.geolocationToPoint(event));
            setInitialScale(initialMapScale);
            updateFocalPoint({
              position: event,
              isHidden: true,
            });
          },
          (error) => {
            clearTimeout(extentZoom);
            zoomToExtent();
          }
        );
      } else {
        clearTimeout(extentZoom);
        zoomToExtent();
      }
    }
  }, [focalPoint, locationsFeatureLayer, mapReady, updateFocalPoint, appConfig?.locations]);

  useEffect(() => {
    if (!mapReady) {
      const ready = focalPoint || (initialCenter && initialScale) || initialExtent;
      setIsMapReady(!!ready);
    }
  }, [focalPoint, initialCenter, initialScale, initialExtent, mapReady]);

  // useEffect(() => {
  //   if (!zoneFL) {
  //     return;
  //   }
  //   if (focalPoint?.geometry === undefined) {
  //     setCurrZone(undefined);
  //   } else {
  //     const zoneFLQuery = zoneFL.createQuery();
  //     zoneFLQuery.geometry = focalPoint?.geometry as Geometry;
  //     zoneFLQuery.outFields = window.activeMapConfig?.zone_layer_out_fields;

  //     zoneFL.queryFeatures(zoneFLQuery).then((res) => {
  //       const values: number[] = [];
  //       const uniqueZones = res.features
  //         .map((item) => {
  //           if (zoneFLQuery.outFields && zoneFLQuery.outFields.length > 0) {
  //             return Number(item.attributes[zoneFLQuery.outFields[0]]);
  //           }
  //           return undefined;
  //         })
  //         .filter((num) => {
  //           if (num === undefined || values.includes(num)) {
  //             return false;
  //           }
  //           values.push(num);
  //           return true;
  //         });
  //       if (uniqueZones.length === 1) {
  //         setCurrZone(uniqueZones[0]);
  //       } else {
  //         setCurrZone(undefined);
  //       }
  //     });
  //   }
  // }, [focalPoint, zoneFL]);

  /**
   * Cooling Centers
   */
  const getBannerProps = () => {
    return undefined;
  };

  /**
   * Hurricane
   */
  useEffect(() => {
    if (t && appConfig?.appId) {
      const bannerTextObj = t(`app.landing.bannerText.${appConfig.appId}`, {
        returnObjects: true,
      });

      // first check if there was an error when getting the event state
      if (eventState === "error") {
        setError(new Error(t("landing.unknownError")));
        setErrorMessage(ErrorTypes.STATUS);
      }

      for (const [k, v] of Object.entries(bannerTextObj)) {
        if (k === eventState) {
          setBannerText(alternateBannerText ? alternateBannerText : v);
        }
      }
    }
  }, [appConfig, t, eventState, alternateBannerText]);

  if (bannerText.includes("{{zones}}")) {
    let zones = "";
    if (evacZones?.length) {
      evacZones.forEach((zone, idx) => {
        if (idx === 0) {
          zones += zone;
        } else if (idx === evacZones.length - 1) {
          zones += t("general.seriesseparatorLastValue", { value: zone });
        } else {
          zones += t("general.seriesseparator", { value: zone });
        }
      });
      setBannerText(bannerText.replace("{{zones}}", zones));
    }
  }

  const hurricaneShowArrayProps = {
    totalZones: 6,
    activeZones: evacZones,
  };

  return (
    <StyledLocationsView>
      <div id="header-container">
        <HeaderBar
          title={alternateAppTitle ? alternateAppTitle : appTitle}
          deactivated={false}
          headerType="locations"
          logoFirst={appConfig?.appInfo.logoFirst}
          showArray={`${appConfig?.appId}` === "hurricane" ? hurricaneShowArrayProps : undefined}
          onHomeClick={() => {
            // check if google translate token exists to fix bug when changing pages
            if (appConfig?.useGoogleTranslate && !getCookie("googtrans")) {
              const select = document.querySelector(".goog-te-combo") as HTMLSelectElement;
              if (select) {
                select.value = "en";
              }
            }

            navigate({
              pathname: "/",
              search: window.location.search,
            });
          }}
          showBanner={getBannerProps()}
        >
          {sourceInfos && (
            <FinderSearch
              mapView={mapView}
              sourceInfos={sourceInfos}
              searchTerm={searchTerm}
              isLanding={false}
              onSearchClear={onSearchClear}
              onSearchComplete={onSearchComplete}
              onSearchBlocked={onSearchBlocked}
              onSearchError={onSearchError}
              appId={appConfig?.appId}
              onEvacZoneUpdate={(zone: number) => {
                setCurrZone(zone);
              }}
            />
          )}
        </HeaderBar>
        {`${appConfig?.appId}` === "hurricane" && (
          <Message zoneNumber={currZone} activeZoneNumber={evacZones} />
        )}
      </div>
      <div id="filter-row-wrapper">
        <StyledFilterH2 className="visually-hidden">Filters</StyledFilterH2>
        <Filter
          locationsFeatureLayer={locationsFeatureLayer}
          booleanFilters={booleanFilters}
          booleanOrFilters={booleanOrFilters}
          categoryFilters={filterFields}
          distanceFilter={distanceFilterItem}
          openHoursServiceTypes={locationLayerInfo.openHoursServiceTypes || []}
          openHoursTimeWindows={locationLayerInfo.openHoursTimeWindows || []}
          onFilterUpdated={updateFilterWhere}
          showOpenNowFilter={appConfig?.filter.showOpenNowFilter || false}
          manyFilters={appConfig?.filter?.manyFilters || false}
          updateGoogleTranslate={updateGoogleTranslate}
          isEmergency={isEmergency}
          appId={appConfig?.appId || "childcarenyc"}
          focalPoint={focalPoint}
        />
      </div>
      {locationId && (
        // must put StyledDetailsWrapper here (or specifically the z-index and position styles)
        // otherwise the list & map will refresh when Details is opened
        <StyledDetailsWrapper>
          <Details
            key={uuidv4()}
            locationId={locationId}
            focalPoint={focalPoint}
            locationsFeatureLayer={locationsFeatureLayer}
            locationLayerInfo={locationLayerInfo}
            updateMapHighlight={updateMapHighlight}
            locationLayerUrl={window.activeMapConfig.loc_layer_url}
            zoneLayerUrl={window.activeMapConfig?.zone_layer_url}
            mapId={window.activeMapConfig.map_id}
            onClose={onCloseDetails}
            onZoomTo={goToMap}
            onCardClick={onClickCardLink}
            onPopupUpdate={onPopupShownHidden}
            detailSections={
              appConfig?.detailSections?.map((item, idx) => {
                const sectionStrings = t(`app.detailSections.${appConfig.appId}`, {
                  returnObjects: true,
                });
                if (item.serviceMinMaxValues?.length) {
                  item.serviceMinMaxValues = item.serviceMinMaxValues.map((m) => {
                    try {
                      m.fieldLabel = fieldLabels[m.serviceLabelFieldname] || "";
                    } catch (err) {
                      m.fieldLabel = "";
                      console.error(err);
                    }
                    return m;
                  });
                }
                const result = {
                  ...item,
                  ...sectionStrings[idx],
                };
                return result;
              }) || []
            }
            updateGoogleTranslate={updateGoogleTranslate}
            fieldLabels={fieldLabels}
            layoutDir={layoutDir}
            isEmergency={isEmergency}
            appId={appConfig?.appId || "childcarenyc"}
          />
        </StyledDetailsWrapper>
      )}
      <div id="page-content">
        <div id="list-col-wrapper" className={!!mView ? "hide" : ""}>
          <StyledListH2 className="visually-hidden">Locations</StyledListH2>
          {appConfig?.list?.showCaveats && (
            <>
              <h3 className="visually-hidden">Additional Location Information</h3>
              <div id="caveats">
                <p>
                  {t(
                    `locations.firstCaveat.${appConfig.appId}.${isEmergency ? "active" : "default"}`
                  )}
                </p>
                <p>
                  {t(
                    `locations.secondCaveat.${appConfig.appId}.${
                      isEmergency ? "active" : "default"
                    }`
                  )}
                </p>
              </div>
            </>
          )}

          {booleanFilters.length !== 0 && (
            <BooleanFilterBanner booleanFilters={booleanFilters} isMapView={!!mView} />
          )}

          <List
            isMapView={!!mView}
            locationsFeatureLayer={locationsFeatureLayer}
            locationsFeatureLayerInfo={locationLayerInfo}
            filterWhere={filterWhere}
            focalPoint={focalPoint}
            onListLoadError={onListError}
            initialSearchBufferDistance={initialSearchBufferDistance}
            onListItemHover={onListItemHover}
            onCardClick={onClickCardLink}
            updateGoogleTranslate={updateGoogleTranslate}
            fieldLabels={fieldLabels}
          />
        </div>

        {mapReady && (
          <>
            <Map
              itemId={window.activeMapConfig?.map_id}
              initialScale={initialMapScale}
              initialCenter={initialCenter}
              initialExtent={initialExtent}
              targetLayerUrl={window.activeMapConfig?.loc_layer_url}
              zoneLayerUrl={window.activeMapConfig?.zone_layer_url}
              locationsFeatureLayerInfo={locationLayerInfo}
              onError={onMapError}
              onMapViewSet={updateMapView}
              locationGraphicsLayerId={MapController.SELECTED_LOCATION_LAYER_ID}
              onCardClick={onClickCardLink}
              onPopupUpdate={onPopupShownHidden}
              onPopupVisibilityChange={onMapPopupVisibilityChange}
              mainMap={true}
              updateGoogleTranslate={updateGoogleTranslate}
              fieldLabels={fieldLabels}
            />
          </>
        )}
        <StyledFabContainer>
            <CalciteFab
              key={mView ? "map" : "list"}
              icon={mView ? "list" : "map-pin"}
              textEnabled={true}
              text={t(`locations.${mView ? "showList" : "showMap"}`)}
              color="blue"
              appearance="solid"
              onClick={switchView}
              style={mView ? { marginBottom: `${popupHeight}px` } : {}}
          ></CalciteFab>
        </StyledFabContainer>
        <ErrorComponent message={errorMessage} error={error} />
      </div>
      <Outlet />
    </StyledLocationsView>
  );
};

export default LocationsPage;
